//import SignalR from '@microsoft/signalr';
import * as signalR from "@microsoft/signalr";
import { MiddlewareAPI } from "redux";
import { SOCKETS_URL } from "../redux/Consts";
import {
  AlertAppID,
  AlertSocket,
  AlertStatus,
  AlertStatusSocket,
  AlertToken,
  IAlertMonitor,
  Reminder,
  ReminderAction,
  fromAlertTokenToAppId,
} from "../redux/alerts";
import {
  getNewAlertBySocket,
  getNewReminderBySocket,
  getReminderHandledBySocket,
  getReminderMissedBySocket,
  getUserNearbyAlertsBySocket,
} from "../redux/alerts/actions";
import { AUTHENTICATION } from "../redux/auth/types";
import { ADDDeviceResidentDTO, DeviceType } from "../redux/devices";
import {
  addDeviceBySocket,
  deleteDeviceBySocket,
  getDeviceStatusBySoket,
  updateDeviceBySocket,
} from "../redux/devices/actions";
import {
  DeviceStatusSocket,
  DeviceStatusesNames,
  DeviceStatusesNamesSocket,
  OccupancyStatus,
  UserNearVitals,
  convertSocketToDeviceStatuses,
  convertWatchStatus,
} from "../redux/devicesStatuses";
import {
  organizationServiceStatusChange,
  residentsServiceStatusChange,
  socketsNotWorking,
  socketsWorking,
} from "../redux/healthCheck/actions";
import { setAlertByTypeSuccess } from "../redux/modalFeedback/actions";
import { setRepeatAlertSockets } from "../redux/repeatAlert/actions";
import { groupRepeatSettings } from "../redux/repeatAlert/selectors";
import {
  ActivityStatus,
  ResidentActivityType,
  SubActivityType,
} from "../redux/residentActivityPlan";
import { getActivityBySocket } from "../redux/residentActivityPlan/actions";
import {
  ActivityStatusSocket,
  BedStatusSocket,
  BedStatusValue,
  ResidentStatusesSocket,
} from "../redux/residentStatuses";
import {
  MoveResidentWithDevicesBySocketDTO,
  changeNodePresenceSocket,
  gender,
  presenceStatuses,
} from "../redux/residents";
import {
  addDeviceToResidentBySocket,
  addResidentBySoket,
  changeStatusBatchBySocket,
  deleteDeviceFromResidentBySocket,
  deleteResidentBySoket,
  geAllResidentRules,
  moveResidentWithDevicesBySocket,
  updateResidentBySoket,
  updateResidentStatusesBySoket,
} from "../redux/residents/action";
import { isResidentPresent } from "../redux/residents/selectors";

import { IAppState } from "../redux/types";
import { changeUserNotificationSockets } from "../redux/userProfile/actions";
import {
  IUserProfileDeleteNodeActionBySockets,
  USER_PROFILE,
} from "../redux/userProfile/types";

import moment from "moment-timezone";
import { notificationAppear } from "../redux/newNotification/actions";
import { NotificationState } from "../redux/newNotification/types";
import { isNodeBelongsToParent } from "../redux/organization/selectors";
import { alertTokenToSound } from "../utils/AlertTo";
import { playNotificationWithRoom } from "../utils/SoundManager";
import { StorageHelper } from "../utils/StorageHelper";
import { InvokeSokets, SName } from "./SocketNames";
import { notificationsSettings } from "../redux/userProfile";
const ServerTimeout = 60 * 1000;
const KeepAliveInterval = 30 * 1000;

const toLoverCaseFirstLetter = (string: string) => {
  return string.charAt(0).toLocaleLowerCase() + string.slice(1);
};
const RECONNECT_TIMEOUT = 1000;
export const socketMiddleware = (url: string) => {
  return (store: MiddlewareAPI<any, IAppState>) => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(SOCKETS_URL(), {
        accessTokenFactory: () => StorageHelper.instance.GetAccessToken(),
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect([
        RECONNECT_TIMEOUT,
        RECONNECT_TIMEOUT,
        RECONNECT_TIMEOUT,
        RECONNECT_TIMEOUT,
      ])
      .build();

    connection.serverTimeoutInMilliseconds = ServerTimeout;
    connection.keepAliveIntervalInMilliseconds = KeepAliveInterval;
    let shouldRestoreConnection = undefined;
    connection.onreconnected(() => {
      setTimeout(() => {
        if (connection.state === signalR.HubConnectionState.Connected)
          store.dispatch(socketsWorking());
      }, RECONNECT_TIMEOUT);
    });
    connection.onreconnecting(() => {
      store.dispatch(socketsNotWorking(""));
    });
    connection.onclose(async (error?: Error) => {
      if (shouldRestoreConnection) {
        store.dispatch(socketsNotWorking(error));
        connectSocket();
      }
    });
    connection.on(SName.ALERT, (alert: AlertSocket) => {
      const newAlert = {
        ...alert,
        nodeId: Number(alert.nodeId),
      };
      switch (alert.status) {
        case AlertStatusSocket.done:
        case AlertStatusSocket.ManualDone:
        case AlertStatusSocket.AutomaticallyDone:
          store.dispatch(
            setAlertByTypeSuccess({
              alertId: alert.alertToken,
              patientId: alert.patientId,
            })
          );
          break;
        case AlertStatusSocket.open:
        case AlertStatusSocket.updated:
          store.dispatch(getNewAlertBySocket(newAlert));
          playSoundIfNeeded(
            newAlert.nodeId,
            alert,
            alertTokenToSound(alert.alertToken),
            fromAlertTokenToAppId(alert.alertToken),
            {
              type: "Alert",
              narrowType: alert.alertToken,
              residentId: alert.patientId,
              userId: "",
            }
          );
          showNotificationIfNeeded(
            newAlert.nodeId,
            newAlert.alertToken,
            newAlert.patientId,
            newAlert.lastUpdate
          );

          break;
        case AlertStatusSocket.Replaces:
          store.dispatch(
            getNewAlertBySocket({
              ...newAlert,
              lastUpdate: new Date().toISOString(),
            })
          );
          playSoundIfNeeded(
            newAlert.nodeId,
            alert,
            alertTokenToSound(alert.alertToken),
            fromAlertTokenToAppId(alert.alertToken),
            {
              type: "Alert",
              narrowType: alert.alertToken,
              residentId: alert.patientId,
              userId: "",
            }
          );
          showNotificationIfNeeded(
            newAlert.nodeId,
            newAlert.alertToken,
            newAlert.patientId,
            newAlert.lastUpdate
          );
          break;
        case AlertStatusSocket.reset:
          if (
            newAlert.alertToken === AlertToken.Fall ||
            newAlert.alertToken === AlertToken.TurnResident
          ) {
            store.dispatch(getNewAlertBySocket(newAlert));
            playSoundIfNeeded(
              newAlert.nodeId,
              alert,
              alertTokenToSound(alert.alertToken),
              fromAlertTokenToAppId(alert.alertToken),
              {
                type: "Alert",
                narrowType: alert.alertToken,
                residentId: alert.patientId,
                userId: "",
              }
            );
            showNotificationIfNeeded(
              newAlert.nodeId,
              newAlert.alertToken,
              newAlert.patientId,
              newAlert.lastUpdate
            );
          }
          break;
        case AlertStatusSocket.canceled:
          store.dispatch(
            setAlertByTypeSuccess({
              alertId: alert.alertToken,
              patientId: alert.patientId,
            })
          );
          break;
        default:
          break;
      }
    });

    connection.on(SName.ACTIVITY, (reminder: ReminderAction) => {
      const newRem = {
        ...reminder,
        nodeId: Number(reminder.nodeId),
      };
      if (newRem.activityStatus.toLowerCase() === "open") {
        const remAct: Reminder = {
          ...reminder,
          lastUpdate: reminder.activityTime,
          nodeId: Number(reminder.nodeId),
          utcDate: reminder.utcActivityTime,
        };

        const isResidentHere = isResidentPresent(
          store.getState(),
          newRem?.residentId
        );

        store.dispatch(getNewReminderBySocket(remAct));

        if (isResidentHere) {
          const soundName =
            newRem.activityType === ResidentActivityType.DRINKING
              ? newRem.activityType + "Reminder.mp3"
              : newRem.activityType + ".mp3";

          playSoundIfNeeded(
            remAct?.nodeId,
            remAct,
            soundName,
            remAct.activityType,
            {
              type: "Reminder",
              narrowType: remAct.activityType,
              residentId: newRem?.residentId,
              userId: "",
            }
          );
          showNotificationIfNeeded(
            remAct.nodeId,
            remAct.activityType,
            remAct.residentId,
            remAct.lastUpdate,
            remAct.activityParams
          );
        }
      }
      if (
        newRem.activityStatus === ActivityStatus.DONE ||
        newRem.activityStatus === ActivityStatus.AutomaticallyDone ||
        newRem.activityStatus === ActivityStatus.ManualDone
      ) {
        store.dispatch(getReminderHandledBySocket(newRem));
      } else if (newRem.activityStatus === ActivityStatus.TIMEOUT) {
        store.dispatch(getReminderMissedBySocket(newRem));
      }

      store.dispatch(getActivityBySocket(newRem));
    });

    connection.on(SName.DEVICE_STATE, (data: DeviceStatusSocket) => {
      try {
        const result = convertSocketToDeviceStatuses(data);

        if (result !== undefined && Object.keys(result).length !== 0)
          store.dispatch(
            getDeviceStatusBySoket({
              deviceId: data.deviceId,
              status: result,
            })
          );

        if (data.type === DeviceStatusesNamesSocket.UserNearby) {
          const legacy = JSON.parse(data.value);
          const nearRoom = legacy?.nearRoom || [];
          const nearBed = [];
          const details: UserNearVitals[] =
            legacy?.nearBed || legacy?.nearRoom
              ? nearBed.concat(nearRoom)
              : (legacy as UserNearVitals[]);
          const device = store.getState().devices.devices[data.deviceId];
          store.dispatch(
            getUserNearbyAlertsBySocket(
              data.deviceId,
              details,
              device?.room,
              device?.bed
            )
          );
          const withAlert = details.filter((el) => el?.alert);
          const secondsPassed = moment().diff(
            moment(withAlert[0].datetime),
            "seconds"
          );
          if (withAlert.length > 0 && secondsPassed < 350) {
            //Un alerts happens in 5 mins
            const constructAlert = {
              room: device?.room,
              bed: device?.bed,
              nodeId: Number(data.nodeId),
              alertToken: AlertToken.UserNearby,
              patientId: device?.residentId,
              patientName: "",
              priority: 2,
              date: details[0].datetime,
              status: AlertStatus.open,
              lastUpdate: details[0].datetime,
            };
            playSoundIfNeeded(
              device.nodeId,
              constructAlert,
              "UserNearby.mp3",
              AlertAppID.UserNearby,
              {
                type: "Alert",
                narrowType: AlertToken.UserNearby,
                residentId: device?.residentId,
                userId: details[0].id,
              }
            );
            showNotificationIfNeeded(
              device.nodeId,
              AlertToken.UserNearby,
              device?.residentId,
              details[0].datetime
            );
          }
        }
      } catch (e) {
        // debugger;
      }
      // console.log(data.type);
    });
    connection.on(
      SName.DEVICE_ADDED,
      (data: {
        id: string;
        hasPump: boolean;
        bed: string;
        room: string;
        node: any;
        resident: any;
        type: string;
        statuses: any;
      }) => {
        store.dispatch(
          addDeviceBySocket({
            id: data.id,
            hasPump: data.hasPump,
            bed: data.bed,
            room: data.room,
            resident: data.resident !== null ? data.resident.id : data.resident,
            nodeId: Number.parseInt(data.node.id),
            type: data.type as DeviceType,
            statuses: data.statuses,
          })
        );
      }
    );
    connection.on(
      SName.DEVICE_DELETED,
      (data: { id: string; nodeId: number }) => {
        store.dispatch(
          deleteDeviceBySocket({ deviceId: data.id, residentId: undefined })
        );
      }
    );

    connection.on(SName.RESIDENT_DEVICE_ASSIGNED, (data) => {
      store.dispatch(addDeviceToResidentBySocket(data as ADDDeviceResidentDTO));
      store.dispatch(
        geAllResidentRules((data as ADDDeviceResidentDTO)?.residentId)
      );
    });

    connection.on(
      SName.RESIDENT_DEVICE_DELETED,
      (data: { deviceId: string; nodeId: number; residentId: string }) => {
        store.dispatch(
          deleteDeviceFromResidentBySocket({
            deviceId: data.deviceId,
            residentId: data?.residentId,
          })
        );
      }
    );
    connection.on(
      SName.DEVICE_UPDATED,
      (data: {
        id: string;
        hasPump: boolean;
        bed: string;
        room: string;
        node: any;
        resident: any;
        type: string;
        statuses: any;
      }) => {
        store.dispatch(
          updateDeviceBySocket({
            id: data.id,
            hasPump: data.hasPump,
            bed: data.bed,
            room: data.room,
            resident: data.resident !== null ? data.resident.id : data.resident,
            nodeId: Number.parseInt(data.node.id),
            type: data.type as DeviceType,
            statuses: data.statuses,
          })
        );
      }
    );

    // connection.on(SName.USER_DELETED, (data: UserIdDTO) => {
    //   const userProfile = store.getState().userProfile;
    //   store.dispatch(deleteUser(data));
    //   if (data.id === userProfile.id) {
    //     store.dispatch(logoutNew("", true));
    //   }
    // });

    connection.on(SName.RESIDENT_ADDED, (data: any) => {
      store.dispatch(addResidentBySoket({ ...data, id: data.self.id }));
    });
    connection.on(SName.RESIDENT_UPDATED, (data: any) => {
      store.dispatch(
        updateResidentBySoket({
          ...data,
          id: data.self.id,
          nodePresence: data.nodePresence.toLowerCase() as presenceStatuses,
          gender: toLoverCaseFirstLetter(data.gender) as gender,
        })
      );
    });

    connection.on(
      SName.RESIDENT_BED_STATUS_CHANGED,
      (data: {
        bedStatus: BedStatusValue;
        nodeId: number;
        residentId: string;
        deviceId: string;
      }) => {
        const bedStatus = {} as ResidentStatusesSocket;
        bedStatus.bedStatus = {
          residentId: data?.residentId,
          status:
            data.bedStatus.substr(0, 1).toLowerCase() +
            data.bedStatus.substr(1),
        } as BedStatusSocket;
        store.dispatch(updateResidentStatusesBySoket(bedStatus));
        const hasOccupancy: OccupancyStatus = {
          id: DeviceStatusesNames.OCCUPANCY,
          value:
            data.bedStatus.substr(0, 1).toLowerCase() +
            data.bedStatus.substr(1),
        } as OccupancyStatus;
        if (hasOccupancy) {
          store.dispatch(
            getDeviceStatusBySoket({
              deviceId: data.deviceId,
              status: hasOccupancy,
            })
          );
        }
      }
    );

    connection.on(SName.RESIDENT_ACTIVE_STATUS_CHANGED, (data: any) => {
      const activityStatus = {} as ResidentStatusesSocket;
      activityStatus.activityStatus = {
        residentId: data?.residentId,
        value: data.activityStatus,
      } as ActivityStatusSocket;
      store.dispatch(updateResidentStatusesBySoket(activityStatus));
    });

    connection.on(
      SName.RESIDENT_WITH_DEVICE_WAS_MOVED,
      (data: MoveResidentWithDevicesBySocketDTO) => {
        store.dispatch(
          moveResidentWithDevicesBySocket({
            ...data,
            fromNodeId: Number(data.fromNodeId),
            toNodeId: Number(data.toNodeId),
          })
        );
      }
    );

    connection.on(
      SName.RESIDENT_DELETED,
      ({ id, nodeId }: { id: string; nodeId: string }) => {
        store.dispatch(
          deleteResidentBySoket({ id, nodeId: Number.parseInt(nodeId) })
        );
      }
    );

    connection.on(SName.OGR_Notification_SETTING_UPDATED, (data: any) => {
      const { organizationId, ...rest } = data;
      const dataUpd = { ...rest, nodeId: organizationId };
      store.dispatch(setRepeatAlertSockets(dataUpd));
    });

    connection.on(
      SName.USER_UPDATE_NOTIFICATION,
      (data: {
        userId: string;
        nodeId: string;
        notification: notificationsSettings;
      }) => {
        store.dispatch(
          changeUserNotificationSockets(data.userId, data.notification)
        );
      }
    );

    connection.on(SName.ROOM_TEMP_CHANGED, (data: DeviceStatusSocket) => {
      store.dispatch(
        getDeviceStatusBySoket({
          deviceId: data.deviceId,
          status: convertSocketToDeviceStatuses({
            ...data,
            type: DeviceStatusesNamesSocket.SENSORS,
          }),
        })
      );
    });

    connection.on(SName.RESIDENTS_ALIVE, (data: any) => {
      const { residentServiceError } = store.getState().healthCheck;
      if (residentServiceError && data) {
        store.dispatch(residentsServiceStatusChange(true));
      }
    });
    connection.on(SName.ORGANIZATION_ALIVE, (data: boolean) => {
      const { organizationServiceError } = store.getState().healthCheck;
      if (organizationServiceError && data) {
        store.dispatch(organizationServiceStatusChange(!data));
      }
    });
    connection.on(
      SName.RESIDENT_NODE_PRESENCE_CHANGED_BATCH,
      (data: changeNodePresenceSocket) => {
        store.dispatch(
          changeStatusBatchBySocket({
            ...data,
            nodePresenceType:
              data.nodePresenceType.toLowerCase() as presenceStatuses,
          })
        );
      }
    );

    connection.on(SName.WATCH_STATUS, (data: any) => {
      const result = convertWatchStatus(data);
      if (result) {
        store.dispatch(
          getDeviceStatusBySoket({
            deviceId: data.deviceId,
            status: result,
          })
        );
      }
    });
    [
      "receiveResidentMovementLevelChanged, receiveResidentVitals",
      "updateUserActivityStatus",
      "Deployment",
    ].map((notUse) =>
      connection.on(notUse, (data: any) => {
        //    store.dispatch(changeParadigmaSyncStatus(data.apiKey, data.statuses));
      })
    );

    const unsubscrubeFromVitalsState = (nodeId: number) => {
      if (
        connection.state !== signalR.HubConnectionState.Connected ||
        nodeId === undefined
      )
        return;
      try {
        connection
          .invoke(InvokeSokets.UNSUBSCRIBE_RESIDENT_VITALS, String(nodeId))
          .catch((reason) => {
            console.log("bad sockets unsubscrube0" + reason);
            store.dispatch(socketsNotWorking(reason));
          });
      } catch (error) {}
    };

    const connectSocket = () => {
      try {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
          connection
            .start()
            .then(() => {
              store.dispatch(socketsWorking());
            })
            .catch((e) => {
              if (shouldRestoreConnection) {
                console.log("SignalR error.");
                if (shouldRestoreConnection === undefined)
                  store.dispatch(socketsNotWorking("dead by cors"));
                setTimeout(connectSocket, RECONNECT_TIMEOUT);
              }
            });
        }
      } catch (err) {}
    };

    const disconnectSocket = async () => {
      try {
        console.log("SignalR stop trying.");
        await connection.stop();
        console.log("SignalR disconnected.");
      } catch (err) {
        //console.log(err);
      }
    };

    const playSoundIfNeeded = (
      notificationNodeId: number,
      notification: AlertSocket | Reminder | IAlertMonitor,
      soundName: string,
      typeToCheck: AlertAppID | ResidentActivityType,
      playParams: {
        type: "Reminder" | "Alert";
        narrowType: AlertToken | ResidentActivityType;
        residentId: string;
        userId: string;
      }
    ) => {
      const nodeId = store.getState().userProfile.monitorNodeId;
      const notificationBelongs = isNodeBelongsToParent(
        store.getState().organization.nodes,
        nodeId,
        Number(notificationNodeId)
      );
      const { language, notifications } = store.getState().userProfile;
      const groupSetting = groupRepeatSettings(
        store.getState(),
        notificationNodeId
      );
      if (
        notificationBelongs &&
        notifications?.push &&
        notifications?.sound &&
        groupSetting?.notifications?.indexOf(typeToCheck) !== -1
      ) {
        playNotificationWithRoom(
          notification,
          soundName,
          language,
          groupSetting?.notificationRepeatIntervalInSeconds !== 0
            ? groupSetting?.notificationRepeatCount
            : undefined,
          groupSetting?.notificationMessageInclude,
          playParams.type,
          playParams.narrowType,
          playParams.residentId,
          playParams.userId
        );
      }
    };

    const showNotificationIfNeeded = (
      notificationNodeId: number,
      type: AlertToken | ResidentActivityType,
      residentId: string,
      datetime: string,
      activityParams: SubActivityType[] = []
    ) => {
      const nodeId = store.getState().userProfile.monitorNodeId;
      const notificationBelongs = isNodeBelongsToParent(
        store.getState().organization.nodes,
        nodeId,
        Number(notificationNodeId)
      );
      const { notifications } = store.getState().userProfile;

      if (notificationBelongs && notifications?.push) {
        store.dispatch(
          notificationAppear({
            residentId,
            type,
            state: NotificationState.new,
            datetime,
            activityParams,
          })
        );
        if (notifications?.vibration) window.navigator.vibrate(200);
      }
    };
    const changeBaseUrl = () => {
      connection
        .stop()
        .then(() => {
          connection.baseUrl = SOCKETS_URL();
        })
        .then(() => {
          connection.start().then(() => {
            store.dispatch(socketsWorking());
            shouldRestoreConnection = true;
          });
        })
        .catch((e) => {
          if (shouldRestoreConnection) {
            console.log("SignalR error.");
            if (shouldRestoreConnection === undefined)
              store.dispatch(socketsNotWorking("dead by cors"));
            setTimeout(connectSocket, RECONNECT_TIMEOUT);
          }
        });
    };
    return (next: (action: any) => void) => (action: any) => {
      switch (action?.type) {
        case USER_PROFILE.SUCCESS: {
          if (connection.state === signalR.HubConnectionState.Connected) {
            store.dispatch(socketsWorking());
            shouldRestoreConnection = true;
            // console.log('shouldRestoreConnection' + shouldRestoreConnection);
          }
          if (connection.state === signalR.HubConnectionState.Disconnected) {
            connection
              .start()
              .then(() => {
                store.dispatch(socketsWorking());
                shouldRestoreConnection = true;
              })
              .catch(() => {
                console.log("SignalR error.");
                if (shouldRestoreConnection === undefined)
                  store.dispatch(socketsNotWorking("dead by cors"));
                setTimeout(connectSocket, RECONNECT_TIMEOUT);
              });
          }
          break;
        }

        case AUTHENTICATION.LOGOUT_NEW_SUCCESS:
          shouldRestoreConnection = false;
          disconnectSocket();
          break;

        case USER_PROFILE.DELETE_NODE_BY_SOCKETS:
          const allPossibleIDs = (
            action as IUserProfileDeleteNodeActionBySockets
          ).payload;
          const monitored = store.getState().userProfile.monitorNodeId;

          if (allPossibleIDs.indexOf(monitored) !== -1) {
            unsubscrubeFromVitalsState(monitored);
          }
          break;
        case AUTHENTICATION.SET_IS_BASE_URL_RETRIEVED: {
          const data = action.payload;
          if (data && connection.baseUrl !== SOCKETS_URL()) {
            changeBaseUrl();
          }
          break;
        }

        default:
          break;
      }
      return next(action);
    };
  };
};
